import logger from '../utils/logger';
// src/components/ErrorBoundary.tsx
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { Button } from './ui/button';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * ERROR BOUNDARY - Capture les erreurs React et affiche un fallback UI
 * 
 * Architecture:
 * - Capture toutes les erreurs React (render, lifecycle, constructeurs)
 * - Log détaillé pour debug
 * - UI de fallback user-friendly
 * - Actions de recovery (reload, home)
 */
class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error details for debugging
    logger.error('[ErrorBoundary] React error caught:', {
      error: error.toString(),
      componentStack: errorInfo.componentStack,
      stack: error.stack,
    });

    this.setState({
      error,
      errorInfo,
    });

    // Report to error tracking service (Sentry, etc.) if configured
    if (typeof window !== 'undefined' && (window as any).Sentry) {
      (window as any).Sentry.captureException(error, {
        contexts: {
          react: {
            componentStack: errorInfo.componentStack,
          },
        },
      });
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default fallback UI
      return (
        <div className="min-h-screen bg-theme-base flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-theme-surface border border-theme-border rounded-2xl shadow-xl p-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 rounded-full bg-red-500/10 flex items-center justify-center">
                <AlertTriangle className="w-10 h-10 text-red-500" />
              </div>
            </div>

            {/* Title */}
            <h1 className="text-3xl font-bold text-theme-primary text-center mb-4">
              Oups, quelque chose s'est mal passé
            </h1>

            {/* Description */}
            <p className="text-theme-secondary text-center mb-8">
              Une erreur inattendue s'est produite. Notre équipe a été notifiée et travaille sur une solution.
            </p>

            {/* Error details (dev only) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
                <h3 className="text-sm font-semibold text-red-500 mb-2">Détails de l'erreur (dev only):</h3>
                <pre className="text-xs text-red-400 overflow-auto max-h-40">
                  {this.state.error.toString()}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                onClick={this.handleReload}
                className="bg-accent-primary hover:bg-accent-primary/90 text-white"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Recharger la page
              </Button>
              
              <Button
                onClick={this.handleGoHome}
                variant="outline"
                className="border-theme-border text-theme-primary hover:bg-theme-hover"
              >
                <Home className="w-4 h-4 mr-2" />
                Retour à l'accueil
              </Button>

              {import.meta.env.DEV && (
                <Button
                  onClick={this.handleReset}
                  variant="outline"
                  className="border-theme-border text-theme-primary hover:bg-theme-hover"
                >
                  Réinitialiser (dev)
                </Button>
              )}
            </div>

            {/* Help text */}
            <p className="text-xs text-theme-secondary text-center mt-8">
              Si le problème persiste, contactez-nous à{' '}
              <a href="mailto:support@clairify.fr" className="text-accent-primary hover:underline">
                support@clairify.fr
              </a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

